/*
 * Product Card Wishlist binder — wires the [data-wishlist] heart on
 * a product card to the Swish SDK via window.BroraWishlist.
 * Kept separate from product-card.js so each file stays ≤ 150 lines.
 * Exposes window.BroraProductCardWishlist.bind(button).
 */
;(function () {
  'use strict';

  // Re-entry guard: this is a plain defer script, not
  // type=module, so it has no module-map dedup — a future re-introduction of a per-card <script>
  // tag for this file would silently resurrect the bug the guard is here to prevent (every add/
  // remove re-syncing every heart on the page once per duplicate tag).
  if (window.BroraProductCardWishlist) return;

  // Hearts with an add/remove in flight — protected from the global sync so a
  // partial server list (mid rapid-add burst) can't clobber their optimistic
  // pressed state. Cleared when each heart's own request settles.
  const pending = new Set();

  // The wishlist is product-level: the app returns the full product on read
  // (see wishlist-client.js), so the heart only forwards the product id from the
  // card's data-* hook — no variant, no DOM scraping of styled markup.
  function readProduct(wishlist) {
    return {
      product_id: Number(wishlist.getAttribute('data-wishlist-product-id')) || undefined
    };
  }

  function matchItem(items, productId) {
    return (items || []).find(function (it) {
      return it && productId && String(it.product_id) === String(productId);
    });
  }

  // Swap the accessible name with pressed state (labels via data-* hooks; no-op when absent).
  function syncLabel(wishlist, pressed) {
    wishlist.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    var label = pressed
      ? wishlist.getAttribute('data-wishlist-label-remove')
      : wishlist.getAttribute('data-wishlist-label-add');
    if (label) wishlist.setAttribute('aria-label', label);
  }

  // Reflect one heart's pressed state against the current wishlist list.
  // Match → aria-pressed + data-wishlist-item-id; no match → clear both, so a
  // removal anywhere (drawer, another card, external) un-fills this heart.
  function syncHeart(wishlist, items) {
    if (pending.has(wishlist)) return;
    const match = matchItem(items, wishlist.getAttribute('data-wishlist-product-id'));
    if (match) {
      syncLabel(wishlist, true);
      if (match.id != null) wishlist.setAttribute('data-wishlist-item-id', String(match.id));
    } else {
      syncLabel(wishlist, false);
      wishlist.removeAttribute('data-wishlist-item-id');
    }
  }

  function bind(wishlist) {
    if (!wishlist) return;
    const productId = wishlist.getAttribute('data-wishlist-product-id');

    if (window.BroraWishlist && window.BroraWishlist.getItems) {
      window.BroraWishlist.getItems().then(function (items) { syncHeart(wishlist, items); }).catch(function () {});
    }

    wishlist.addEventListener('click', async function (e) {
      e.preventDefault();
      const pressed = wishlist.getAttribute('aria-pressed') === 'true';
      syncLabel(wishlist, !pressed);
      if (!window.BroraWishlist) return;
      pending.add(wishlist);
      try {
        if (pressed) {
          const itemId = wishlist.getAttribute('data-wishlist-item-id');
          if (itemId) {
            await window.BroraWishlist.removeItem(itemId);
            wishlist.removeAttribute('data-wishlist-item-id');
          } else {
            const items = await window.BroraWishlist.getItems();
            const match = matchItem(items, productId);
            if (match && match.id != null) await window.BroraWishlist.removeItem(match.id);
          }
        } else {
          const payload = readProduct(wishlist);
          const next = await window.BroraWishlist.addItem(payload);
          const match = matchItem(next, productId);
          if (match && match.id != null) wishlist.setAttribute('data-wishlist-item-id', String(match.id));
        }
      } catch (err) {
        syncLabel(wishlist, pressed);
      } finally {
        pending.delete(wishlist);
      }
    });
  }

  // One page-level listener: any add/remove/clear emits brora:wishlist:changed
  // → re-sync every heart in the DOM (handles removals from the drawer, another
  // card, or an external change, plus cards re-rendered by PLP filters).
  document.addEventListener('brora:wishlist:changed', function (e) {
    const items = (e && e.detail && e.detail.items) || [];
    document.querySelectorAll('[data-wishlist], [data-wishlist-add]').forEach(function (w) { syncHeart(w, items); });
  });

  window.BroraProductCardWishlist = { bind: bind };
})();
